import { useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  matchesSessionSearch,
  redactSignInEmail,
  sessionAccountSubtitle,
  sessionActivityTone,
  sessionPersonTitle,
  uniqueAccountNames,
} from "../data/platform-session-visibility";
import {
  fetchPlatformSessions,
  fetchRevocationStatuses,
  queueRevokeSession,
  queueRevokeUserSessions,
  summarizePlatformSessions,
  summarizeRevocationRequests,
  type PlatformSessionRow,
  type RevocationRequestStatus,
} from "../data/platform-sessions";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime, formatRelativeTime } from "../lib/display-time";

// Platform → Devices and sessions — founder breach-response surface with person visibility.
// Honesty rules baked in:
//   - revocation is QUEUED for the security worker; chips track the ledger lifecycle via the
//     Task 111 status RPC (queued → processing → succeeded/failed) and only say "Signed out"
//     on TERMINAL worker success (Codex copy boundary, 03ac0f7);
//   - polling runs only while this surface is mounted, one batched call per tick, and stops by
//     itself once every tracked request is terminal (pooler-respectful);
//   - the signed-in platform user's own sessions get no revoke button (the server rejects
//     self-revocation from this path — we don't offer what can't happen);
//   - rows show display names + workspace names; sign-in email stays behind explicit reveal;
//     full IP remains visible (platform-role-gated; listing is audited server-side).
// Deliberately NO analytics events from this surface — security operations aren't product telemetry.

interface TrackedRequest {
  requestId: string | null;
  status: RevocationRequestStatus["status"];
  message: string | null;
}

const TRACK_COPY: Record<RevocationRequestStatus["status"], string> = {
  queued: "Queued for security worker",
  processing: "Processing…",
  succeeded: "Signed out",
  failed: "Failed — needs review",
};

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function PlatformSessions({
  client,
  selfUserId,
  timeZone,
  pollMs = 30000,
  statusPollMs = 10000,
}: {
  client: SupabaseClient;
  /** The signed-in platform user — their own sessions are not revocable from here. */
  selfUserId: string;
  timeZone: string;
  pollMs?: number;
  /** Revocation-status poll cadence while requests are non-terminal. */
  statusPollMs?: number;
}) {
  const { status, data, error, reload } = useReadModel<PlatformSessionRow[]>(
    () => fetchPlatformSessions(client),
    [client],
    pollMs,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [revealedUserIds, setRevealedUserIds] = useState<ReadonlySet<string>>(() => new Set());
  const [nowMs, setNowMs] = useState(() => Date.now());
  // sessionRef (or "user:<id>") → lifecycle: confirming → queueing → tracked ledger status.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [tracked, setTracked] = useState<Record<string, TrackedRequest>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const trackedRef = useRef(tracked);
  trackedRef.current = tracked;

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Poll the Task 111 read model only while mounted AND something is still in flight; one
  // batched call per tick. Terminal statuses stop the work on their own.
  useEffect(() => {
    const timer = setInterval(() => {
      const pending = Object.values(trackedRef.current)
        .filter((entry) => entry.requestId !== null && (entry.status === "queued" || entry.status === "processing"))
        .map((entry) => entry.requestId as string);
      if (pending.length === 0) {
        return;
      }
      void fetchRevocationStatuses(client, pending).then((statuses) => {
        if (statuses.length === 0) {
          return;
        }
        const byId = new Map(statuses.map((row) => [row.requestId, row]));
        setTracked((previous) => {
          const next = { ...previous };
          for (const [key, entry] of Object.entries(next)) {
            const update = entry.requestId === null ? undefined : byId.get(entry.requestId);
            if (update !== undefined) {
              next[key] = { requestId: entry.requestId, status: update.status, message: update.workerMessage };
            }
          }
          return next;
        });
      });
    }, statusPollMs);
    return () => clearInterval(timer);
  }, [client, statusPollMs]);

  async function runQueued(
    key: string,
    action: () => Promise<{ ok: boolean; requestId?: string | null }>,
  ): Promise<void> {
    setActionError(null);
    setBusyKey(key);
    const result = await action();
    setBusyKey(null);
    setConfirming(null);
    if (result.ok) {
      setTracked((previous) => ({
        ...previous,
        [key]: { requestId: result.requestId ?? null, status: "queued", message: null },
      }));
      reload();
    } else {
      setActionError((result as { reason?: string }).reason ?? "The request was not performed.");
    }
  }

  function trackChip(entry: TrackedRequest) {
    const tone = entry.status === "succeeded" ? " sessions__queued--ok" : entry.status === "failed" ? " sessions__queued--warn" : "";
    return (
      <span className={`sessions__queued${tone}`} role="status">
        {TRACK_COPY[entry.status]}
        {entry.status === "failed" && entry.message !== null ? ` · ${entry.message}` : ""}
      </span>
    );
  }

  const allRows = data ?? [];
  const visibleRows = useMemo(
    () => allRows.filter((row) => matchesSessionSearch(row, searchQuery)),
    [allRows, searchQuery],
  );
  const accountNameRollup = useMemo(() => uniqueAccountNames(allRows), [allRows]);

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading active sessions…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load active sessions."}
      </p>
    );
  }

  if (allRows.length === 0) {
    return <p className="surface__state" role="status">No active sessions right now.</p>;
  }

  const accountHealth = summarizePlatformSessions(allRows, accountNameRollup);
  const revocationHealth = summarizeRevocationRequests(Object.values(tracked));
  const accountMetricLabel = accountNameRollup.length > 0 ? "workspace" : "tenant tail";

  return (
    <div className="sessions">
      <p className="sessions__note">
        Active sessions are valid sign-ins; activity is as recent as each session's last token
        refresh. Names and workspaces help you identify who is signed in. Sign-in email stays
        hidden until you reveal it. Revocations are queued for the security worker — they are not instant.
      </p>
      <div className="sessions__toolbar">
        <label className="sessions__search">
          <span className="sessions__search-label">Find person</span>
          <input
            type="search"
            value={searchQuery}
            placeholder="Name, workspace, email, role, surface, or IP"
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
        {searchQuery.trim().length > 0 ? (
          <p className="sessions__search-meta" role="status">
            {pluralize(visibleRows.length, "match")} of {pluralize(allRows.length, "session")}
          </p>
        ) : null}
      </div>
      <div className="sessions__dashboard" aria-label="Platform account health">
        <section className="sessions__panel" aria-labelledby="account-health-title">
          <div className="sessions__panel-head">
            <h2 id="account-health-title">Account health</h2>
            <p>Derived from active-session rows.</p>
          </div>
          <dl className="sessions__metrics">
            <div>
              <dt>Sessions</dt>
              <dd>{pluralize(accountHealth.activeSessions, "active session")}</dd>
            </div>
            <div>
              <dt>Workspaces</dt>
              <dd>{pluralize(accountHealth.accountCount, accountMetricLabel)}</dd>
            </div>
            <div>
              <dt>Roles</dt>
              <dd>{pluralize(accountHealth.roleCount, "role")}</dd>
            </div>
            <div>
              <dt>Surfaces</dt>
              <dd>{pluralize(accountHealth.surfaceCount, "surface")}</dd>
            </div>
          </dl>
          <p className="sessions__meta">
            Workspaces {accountHealth.accountTails.join(", ")} · Roles {accountHealth.roles.join(", ")} · Surfaces{" "}
            {accountHealth.surfaces.join(", ")}
          </p>
        </section>
        <section className="sessions__panel" aria-labelledby="revocation-health-title">
          <div className="sessions__panel-head">
            <h2 id="revocation-health-title">Queued revocation status</h2>
            <p>Worker lifecycle, not an instant sign-out claim.</p>
          </div>
          <dl className="sessions__metrics sessions__metrics--revocations">
            <div>
              <dt>Queued</dt>
              <dd>{pluralize(revocationHealth.queued, "queued revocation")}</dd>
            </div>
            <div>
              <dt>Processing</dt>
              <dd>{pluralize(revocationHealth.processing, "processing request")}</dd>
            </div>
            <div>
              <dt>Completed</dt>
              <dd>{pluralize(revocationHealth.succeeded, "completed revocation")}</dd>
            </div>
            <div>
              <dt>Failed</dt>
              <dd>{pluralize(revocationHealth.failed, "failed request")}</dd>
            </div>
          </dl>
        </section>
        <section className="sessions__panel sessions__panel--proof" aria-labelledby="bounded-access-title">
          <div className="sessions__panel-head">
            <h2 id="bounded-access-title">Bounded founder access</h2>
            <p>Server-gated platform proof.</p>
          </div>
          <ul className="sessions__proof">
            <li><code>platform_active_sessions</code> requires platform role and is audited server-side.</li>
            <li>Sign-in email renders only after explicit reveal; no tokens or user-agent strings in this surface.</li>
            <li>Revocation remains queued until worker status proves a terminal outcome.</li>
          </ul>
        </section>
      </div>
      {actionError ? (
        <p className="surface__state surface__state--error" role="alert">
          {actionError}
        </p>
      ) : null}
      {visibleRows.length === 0 ? (
        <p className="surface__state" role="status">No sessions match that search.</p>
      ) : (
        <table className="sessions__table">
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Role</th>
              <th scope="col">Workspace</th>
              <th scope="col">Surface</th>
              <th scope="col">Signed in</th>
              <th scope="col">Last active</th>
              <th scope="col">IP</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const isSelf = row.userId === selfUserId;
              const sessionTracked = tracked[row.sessionRef];
              const userKey = `user:${row.userId}`;
              const userTracked = tracked[userKey];
              const contactRevealed = revealedUserIds.has(row.userId);
              const accountSubtitle = sessionAccountSubtitle(row);
              const activityTone = sessionActivityTone(row.lastRefreshedAt, nowMs);
              return (
                <tr key={row.sessionRef}>
                  <td className="sessions__person">
                    <strong className="sessions__person-name">{sessionPersonTitle(row)}</strong>
                    {accountSubtitle !== null ? (
                      <span className="sessions__person-workspace">{accountSubtitle}</span>
                    ) : null}
                    <span className="sessions__person-email">
                      {redactSignInEmail(row.signInEmail, contactRevealed)}
                    </span>
                    {row.signInEmail !== null ? (
                      <button
                        type="button"
                        className="sessions__reveal"
                        aria-pressed={contactRevealed}
                        onClick={() =>
                          setRevealedUserIds((current) => {
                            const next = new Set(current);
                            if (next.has(row.userId)) {
                              next.delete(row.userId);
                            } else {
                              next.add(row.userId);
                            }
                            return next;
                          })
                        }
                      >
                        {contactRevealed ? "Hide sign-in email" : "Reveal sign-in email"}
                      </button>
                    ) : null}
                    <span className="sessions__person-legacy">{row.userLabel}</span>
                  </td>
                  <td>
                    <span className={`sessions__chip sessions__chip--${row.role}`}>{row.role}</span>
                  </td>
                  <td>{row.accountNames ?? row.accountTail}</td>
                  <td>{row.surfaceHint}</td>
                  <td>{row.createdAt ? formatDisplayTime(row.createdAt, timeZone) : "—"}</td>
                  <td>
                    {row.lastRefreshedAt ? (
                      <span className={`sessions__activity sessions__activity--${activityTone}`}>
                        {formatRelativeTime(row.lastRefreshedAt, nowMs, timeZone)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="sessions__ip">{row.ip ?? "—"}</td>
                  <td className="sessions__actions">
                    {isSelf ? (
                      <span className="sessions__self">This is you</span>
                    ) : sessionTracked !== undefined ? (
                      trackChip(sessionTracked)
                    ) : confirming === row.sessionRef ? (
                      <>
                        <button
                          className="sessions__danger"
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => void runQueued(row.sessionRef, () => queueRevokeSession(client, row.sessionRef))}
                        >
                          {busyKey === row.sessionRef ? "Queueing…" : "Confirm queue revoke"}
                        </button>
                        <button
                          className="sessions__cancel"
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="sessions__action"
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => setConfirming(row.sessionRef)}
                        >
                          Queue revoke
                        </button>
                        {userTracked !== undefined ? (
                          trackChip(userTracked)
                        ) : confirming === userKey ? (
                          <>
                            <button
                              className="sessions__danger"
                              type="button"
                              disabled={busyKey !== null}
                              onClick={() => void runQueued(userKey, () => queueRevokeUserSessions(client, row.userId))}
                            >
                              {busyKey === userKey ? "Queueing…" : "Confirm sign out everywhere"}
                            </button>
                            <button
                              className="sessions__cancel"
                              type="button"
                              disabled={busyKey !== null}
                              onClick={() => setConfirming(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="sessions__action"
                            type="button"
                            disabled={busyKey !== null}
                            onClick={() => setConfirming(userKey)}
                          >
                            Sign out everywhere
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
